import { Link } from 'react-router-dom';
import { Store, FileUp, CalendarClock, ListChecks, ReceiptText, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { ManagerSeatRequired, SeatLoading } from '../../components/nursery/retail/ManagerSeatRequired';

/**
 * /nursery/retailers — one place for the work a large retailer (Bunnings
 * first) brings: its purchase orders, its cross dock pick days and the range
 * file its stores order from. They were three cards on the nursery dashboard;
 * here each says what it is for. Remittances are the Accounts module's and
 * show only to someone with an accounts seat.
 */

const TOOLS = [
  {
    label: 'Import purchase orders',
    icon: FileUp,
    path: '/nursery/orders/import',
    detail: 'Upload the store purchase order files. Each is checked against our prices and stock before anything is entered.',
  },
  {
    label: 'Cross dock schedules',
    icon: CalendarClock,
    path: '/nursery/cross-dock',
    detail: 'Which days each store’s order is made up, and the same-day cut-off.',
  },
  {
    label: 'Range upload',
    icon: ListChecks,
    path: '/nursery/retailer-range',
    detail: 'The file that tells the retailer’s app which of our trees stores can order now.',
  },
];

const REMITTANCES = {
  label: 'Rebate claims and remittances',
  icon: ReceiptText,
  path: '/accounts/remittances',
  detail: 'Check the month’s rebate claims and each remittance against our invoices.',
};

function RetailerHub() {
  const { hasAccess: canManage, loading } = useModuleLicense('nursery', ['manager', 'admin']);
  const { hasAccess: hasAccounts } = useModuleLicense('accounts', ['user', 'admin']);

  if (loading) return <SeatLoading />;
  if (!canManage) return <ManagerSeatRequired what="Retailer orders are handled" />;

  const tools = hasAccounts ? [...TOOLS, REMITTANCES] : TOOLS;

  return (
    <>
      <PageHeader
        title="Retailers"
        subtitle="Purchase orders, cross dock and range files for large retailers"
        icon={<Store className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
        {tools.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.path} to={t.path}
              className="flex items-start gap-3 bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-sm transition-all">
              <Icon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-neutral-800">{t.label}</span>
                <span className="block text-sm text-neutral-500 mt-0.5">{t.detail}</span>
              </span>
              <ChevronRight className="h-5 w-5 text-neutral-400 flex-shrink-0 self-center" />
            </Link>
          );
        })}
      </div>
    </>
  );
}

export default RetailerHub;
